import React from "react";
import { CaregiverId, components } from "../../../shared/schema/schema";

type CoServicePlanImportStagedShift = components["schemas"]["CoServicePlanImportStagedShift"];
type CoServicePlanImportLine = components["schemas"]["CoServicePlanImportLine"];
type ReplaceServicePlanScheduleShift = components["schemas"]["ReplaceServicePlanScheduleShift"];
type ShiftDuty = components["schemas"]["CoServicePlanImportShiftDuty"];

/**
 * One shift while a coordinator is still moving it around.
 *
 * `id` is client-side and exists only so React can key the cards and an edit can
 * name the shift it changes. The server takes the whole week and matches nothing by
 * identity, so it is never sent.
 *
 * `caregiverId` and `serviceCode` are nullable here and not in the request: a shift
 * being built is allowed to be incomplete, and Submit is what refuses one that still
 * is.
 */
export interface DraftShift {
  id: string;
  /** MONDAY..SUNDAY, and sent as exactly that — the wire type is a plain string. */
  dayOfWeek: string;
  startTime: string;
  endTime: string;
  caregiverId: CaregiverId | null;
  serviceCode: string | null;
  duties: ShiftDuty[];
  /** Added in this sitting, as opposed to seeded from the staged week. */
  isNew: boolean;
}

export interface ScheduleEditorDraft {
  shifts: DraftShift[];
  isDirty: boolean;
  addShift: (dayOfWeek: string) => void;
  updateShift: (id: string, patch: Partial<DraftShift>) => void;
  removeShift: (id: string) => void;
  toRequestShifts: () => ReplaceServicePlanScheduleShift[];
}

/** What a shift a coordinator has just added starts out as, before they say otherwise. */
const NEW_SHIFT_START = "09:00";
const NEW_SHIFT_END = "13:00";

/**
 * The staged week, as something that can be edited.
 *
 * Seeded once, on mount: the modal is mounted only while it is open, so reopening it
 * re-reads the week and a refetch underneath a coordinator cannot wipe what they have
 * typed.
 *
 * `lines` is needed to seed at all. A staged shift records the AUTHORIZATION it bills
 * against, while the editor asks a coordinator for the SERVICE CODE — the question
 * they can answer, and the one that filters the duty picker — so the line that
 * resolved to that authorization is what says which code a staged shift already has.
 */
export function useScheduleEditorDraft(
  staged: CoServicePlanImportStagedShift[],
  lines: CoServicePlanImportLine[]
): ScheduleEditorDraft {
  const [seeded] = React.useState(() => seed(staged, lines));
  const [shifts, setShifts] = React.useState<DraftShift[]>(seeded);

  const addShift = React.useCallback((dayOfWeek: string) => {
    setShifts((current) => [
      ...current,
      {
        id: crypto.randomUUID(),
        dayOfWeek,
        startTime: NEW_SHIFT_START,
        endTime: NEW_SHIFT_END,
        caregiverId: null,
        serviceCode: null,
        duties: [],
        isNew: true,
      },
    ]);
  }, []);

  const updateShift = React.useCallback((id: string, patch: Partial<DraftShift>) => {
    setShifts((current) =>
      current.map((shift) => {
        if (shift.id !== id) {
          return shift;
        }
        // A duty belongs to a service line, so the duties of the line being left
        // cannot survive onto the one being chosen. Cleared here rather than in the
        // card so the invariant holds however the shift is changed — unless the same
        // patch names the duties itself, which is a caller saying what it wants.
        const changedLine =
          patch.serviceCode !== undefined &&
          patch.serviceCode !== shift.serviceCode &&
          patch.duties === undefined;

        return {
          ...shift,
          ...patch,
          duties: sortDuties(changedLine ? [] : patch.duties ?? shift.duties),
        };
      })
    );
  }, []);

  const removeShift = React.useCallback((id: string) => {
    setShifts((current) => current.filter((shift) => shift.id !== id));
  }, []);

  const toRequestShifts = React.useCallback(
    (): ReplaceServicePlanScheduleShift[] =>
      shifts.map((shift) => {
        // Submit is disabled until every shift is complete, so reaching this is a
        // bug in the modal rather than something a coordinator can do.
        if (shift.caregiverId === null || shift.serviceCode === null) {
          throw new Error("Every shift needs a caregiver and a service code before it is sent");
        }

        return {
          dayOfWeek: shift.dayOfWeek,
          startTime: shift.startTime,
          endTime: shift.endTime,
          caregiverId: shift.caregiverId,
          serviceCode: shift.serviceCode,
          // The count the coordinator set, not a flattened 1: a duty due twice on a
          // day is one shift performing it twice. The server derives the plan of
          // care's visitsPerWeek and timesPerDay from these counts, so sending 1
          // where the week says 2 would halve the care that gets planned.
          duties: shift.duties.map((duty) => ({
            dutyCode: duty.dutyCode,
            occurrences: duty.occurrences,
          })),
        };
      }),
    [shifts]
  );

  return {
    shifts,
    isDirty: canonical(shifts) !== canonical(seeded),
    addShift,
    updateShift,
    removeShift,
    toRequestShifts,
  };
}

function seed(
  staged: CoServicePlanImportStagedShift[],
  lines: CoServicePlanImportLine[]
): DraftShift[] {
  // First line wins, the same way the schedulers resolved it: two lines can only
  // share an authorization when they are the same billable thing under two codes.
  const serviceCodeByAuthorization = new Map<number, string>();
  for (const line of lines) {
    if (line.authorizationId !== null && !serviceCodeByAuthorization.has(line.authorizationId)) {
      serviceCodeByAuthorization.set(line.authorizationId, line.serviceCode);
    }
  }

  return staged.map((shift) => ({
    id: crypto.randomUUID(),
    dayOfWeek: shift.dayOfWeek.toUpperCase(),
    startTime: shift.startTime,
    endTime: shift.endTime,
    // The staged week carries a bare number; the request wants the branded id the
    // submission's own caregivers are listed under.
    caregiverId: shift.caregiverId === null ? null : (shift.caregiverId as CaregiverId),
    serviceCode:
      shift.authorizationId === null
        ? null
        : serviceCodeByAuthorization.get(shift.authorizationId) ?? null,
    duties: sortDuties(shift.duties),
    isNew: false,
  }));
}

/**
 * Duties in one order always, so that picking two and then picking them again in the
 * other order is not mistaken for a change.
 *
 * `occurrences` is carried through as the week is seeded. A duty due several times on
 * a day belongs on ONE shift with a count -- the scheduler no longer manufactures a
 * second visit per occurrence -- so flattening it here would quietly halve the week
 * the first time a coordinator opened the editor and saved.
 */
function sortDuties(duties: ShiftDuty[]): ShiftDuty[] {
  return [...duties].sort((a, b) => a.dutyCode - b.dutyCode);
}

/** Everything about the week that is sent, and nothing that is not. */
function canonical(shifts: DraftShift[]): string {
  return JSON.stringify(
    shifts.map((shift) => [
      shift.id,
      shift.dayOfWeek,
      shift.startTime,
      shift.endTime,
      shift.caregiverId,
      shift.serviceCode,
      // The duty codes AND their counts. How many times a duty is performed on a
      // shift is a coordinator's to change, so a week that differs only by a count
      // is a real edit and must leave the modal dirty.
      shift.duties.map((duty) => [duty.dutyCode, duty.occurrences]),
    ])
  );
}
